// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { Fragment, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';

import type { ProjectInfo } from '../../../api/types';
import { useCanEdit } from '../../../app/CaseRoleContext';
import {
  SAGSDATA_FIELDS,
  SAGSDATA_GROUPS,
  bekNote,
  buildPatch,
  dateInputFits,
  deadline,
  draftOf,
  extrasOf,
  heldLocally,
  isDirty,
  isIsoDate,
  localAfterSave,
  mergeExtras,
  parseArea,
  type SagsDraft,
  type SagsFieldSpec,
  type SagsKey,
  type SagsPatch,
} from '../../../bygning/sagsdata';
import { danishDate, type ProjectPatch } from '../../../overblik/model';
import { Button } from '../../Button';
import { Spinner } from '../../Spinner';
import { TextArea, TextInput } from '../../TextInput';
import { FormDialog } from '../../kortlaegning/FormDialog';
import { Portal } from '../inspector/Portal';
import type { SagsdataExtrasState } from './useSagsdataExtras';
import styles from './SagsdataForm.module.css';

export interface SagsdataFormProps {
  project: ProjectInfo | null;
  /** The case store's first load has landed. */
  loaded: boolean;
  /** `useSagsdataExtras(project)`, held by the host so the Sagen panel and the form read one copy. */
  extras: SagsdataExtrasState;
  /**
   * `CaseActions.patchProject`: queued on the app write chain, toasts a
   * failure (`saveErrorMessage`) and resolves null; mints the record id on
   * the first save of a case without one.
   */
  onSave: (patch: ProjectPatch) => Promise<ProjectInfo | null>;
  /** Closes the dialog (`?edit=` cleared). */
  onClose: () => void;
}

type Status =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved'; localOnly: number }
  | { kind: 'error' }
  | { kind: 'invalid' }
  | { kind: 'restored' };

/** Fields that take the dialog's full width; the rest pair up two by two on a wide screen. */
const WIDE: ReadonlySet<SagsKey> = new Set(['name', 'building_address', 'client', 'notes']);

/**
 * An unsaved draft left behind when the dialog closed (Luk, Esc, the
 * backdrop, Back — there is no confirm). Opening it again for the same
 * record restores it; a reload or closing the tab is guarded by
 * `beforeunload`.
 */
let parked: { key: string; draft: SagsDraft } | null = null;

function statusText(s: Status): string {
  switch (s.kind) {
    case 'saving':
      return 'Gemmer…';
    case 'saved':
      return s.localOnly === 0
        ? 'Gemt.'
        : `Gemt. ${s.localOnly === 1 ? '1 felt er' : `${s.localOnly} felter er`} kun gemt i denne browser — ikke gemt af serveren endnu.`;
    case 'error':
      return 'Kunne ikke gemme. Dine ændringer er ikke tabt — prøv igen.';
    case 'invalid':
      return 'Ret de markerede felter, før du gemmer.';
    case 'restored':
      return 'Dine ændringer fra før er hentet frem. De er ikke gemt endnu.';
    case 'idle':
      return '';
  }
}

/**
 * Sagsdata (v8 prototype), as a dialog over Overblik (`?edit=sag`): the case
 * record and the BEK 496 fields in one fieldset per group, saved together
 * with Gem (or Ctrl/⌘+Enter) and dropped with Fortryd. A save that lands
 * closes the dialog, as the other form dialogs do — unless a field was typed
 * into while it was in flight, or a value is kept only in this browser. The
 * floor area says live whether a demolition plan is required; the start date
 * says when the plan is due. Fields an older ruxd cannot store (contract gap
 * 3) are sent anyway and kept in this browser when the server drops them,
 * marked "ikke gemt af serveren endnu". A viewer gets the same dialog
 * read-only, with Luk alone.
 *
 * Esc closes from anywhere, a field included (FormDialog's keys): the draft is
 * parked, not lost. Focus moves to the first field (Luk for a viewer) and
 * returns to whatever opened the dialog.
 */
export function SagsdataForm({ project, loaded, extras: extrasState, onSave, onClose }: SagsdataFormProps) {
  const canEdit = useCanEdit();
  const id = useId();
  const { extras, local, held, leftover, save } = extrasState;
  const base = useMemo(() => draftOf(project, extras), [project, extras]);
  const parkKey = project?.id ?? '';

  // Read the parked draft without consuming it here (StrictMode runs initialisers twice).
  const [restored] = useState(() => (parked && parked.key === parkKey ? parked.draft : null));
  const [draft, setDraft] = useState<SagsDraft>(() => restored ?? base);
  const [errors, setErrors] = useState<Partial<Record<SagsKey, string>>>({});
  const [status, setStatus] = useState<Status>(() => (restored && isDirty(restored, base) ? { kind: 'restored' } : { kind: 'idle' }));
  const dirty = isDirty(draft, base);
  const saving = status.kind === 'saving';

  const draftRef = useRef(draft);
  draftRef.current = draft;
  const latest = useRef({ dirty, parkKey });
  latest.current = { dirty, parkKey };
  const mountedRef = useRef(true);
  /** Set when a save closes the dialog: nothing is left to park. */
  const savedAndClosing = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    parked = null;
    return () => {
      mountedRef.current = false;
      if (latest.current.dirty && !savedAndClosing.current) parked = { key: latest.current.parkKey, draft: draftRef.current };
    };
  }, []);

  // Focus moves into the dialog (the first field; Luk for a viewer or while
  // loading) and back to the opener when it closes.
  const entryId = useRef('');
  entryId.current = canEdit && loaded ? `${id}-name` : `${id}-close`;
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null;
    document.getElementById(entryId.current)?.focus();
    return () => {
      if (opener?.isConnected) opener.focus();
    };
  }, []);

  // A new server value (a re-read, the first load) replaces an untouched draft; never one being edited.
  const prevBase = useRef(base);
  useEffect(() => {
    if (!isDirty(draftRef.current, prevBase.current)) setDraft(base);
    prevBase.current = base;
  }, [base]);

  // Values this browser kept while the server could not store them (an older
  // ruxd) come back once as unsaved changes, so one Gem moves them to the
  // server; an edit already under way is left alone.
  const leftoverApplied = useRef(false);
  useEffect(() => {
    if (leftoverApplied.current || Object.keys(leftover).length === 0) return;
    leftoverApplied.current = true;
    if (isDirty(draftRef.current, base)) return;
    const withLeftover = draftOf(project, { ...extras, ...leftover });
    if (!isDirty(withLeftover, base)) return;
    setDraft(withLeftover);
    setStatus({ kind: 'restored' });
  }, [leftover, base, project, extras]);

  // Closing the tab or reloading with unsaved changes asks first.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  function change(key: SagsKey, value: string) {
    setDraft((d) => ({ ...d, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
    if (status.kind === 'saved' || status.kind === 'restored') setStatus({ kind: 'idle' });
  }

  function undo() {
    setDraft(base);
    setErrors({});
    setStatus({ kind: 'idle' });
  }

  async function submit() {
    if (!canEdit || saving || !dirty) return;
    const built = buildPatch(draft, base);
    const invalid = Object.keys(built.errors) as SagsKey[];
    if (invalid.length > 0) {
      setErrors(built.errors);
      setStatus({ kind: 'invalid' });
      document.getElementById(`${id}-${invalid[0]}`)?.focus();
      return;
    }
    setErrors({});
    const patch: SagsPatch = built.patch;
    if (Object.keys(patch).length === 0) return;
    const submitted = draft;
    const localBefore = local;
    setStatus({ kind: 'saving' });
    // The extras ride along: `SagsPatch` is a `ProjectPatch` plus the gap-3 keys.
    const body = await onSave(patch);
    if (!body) {
      if (mountedRef.current) setStatus({ kind: 'error' });
      return;
    }
    const nextLocal = localAfterSave(localBefore, extrasOf(patch), body);
    // Persist before anything else: the dialog may have closed meanwhile.
    save(body.id, nextLocal);
    if (!mountedRef.current) return;
    const localOnly = heldLocally(body, nextLocal).length;
    const typedSince = SAGSDATA_FIELDS.some((spec) => draftRef.current[spec.key] !== submitted[spec.key]);
    if (!typedSince && localOnly === 0) {
      savedAndClosing.current = true;
      onClose();
      return;
    }
    const fresh = draftOf(body, mergeExtras(body, nextLocal));
    // A field typed into while the save was in flight keeps what was typed.
    setDraft((now) => {
      const out = { ...fresh };
      for (const spec of SAGSDATA_FIELDS) if (now[spec.key] !== submitted[spec.key]) out[spec.key] = now[spec.key];
      return out;
    });
    setStatus({ kind: 'saved', localOnly });
  }

  const close = (
    <Button id={`${id}-close`} size="lg" onClick={onClose}>
      Luk
    </Button>
  );

  if (!loaded) {
    return (
      <Portal>
        <FormDialog title="Sagsdata" className={styles.dialog} onCancel={onClose} onSubmit={() => {}} actions={close}>
          <Spinner label="Indlæser sagsdata…" />
        </FormDialog>
      </Portal>
    );
  }

  const area = parseArea(draft.demolished_floor_area_m2)?.value ?? null;
  const bek = bekNote(area);
  const due = isIsoDate(draft.demolition_start) ? deadline(draft.demolition_start) : null;
  const message = statusText(status);

  function renderField(spec: SagsFieldSpec) {
    const fieldId = `${id}-${spec.key}`;
    const errorId = `${fieldId}-error`;
    const hintId = `${fieldId}-hint`;
    const error = errors[spec.key];
    const value = draft[spec.key];
    const hint = spec.key === 'demolition_start' && due ? `Plan til kommunen senest ${danishDate(due)}` : null;
    const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined;
    const common = {
      id: fieldId,
      value,
      'aria-invalid': error ? true : undefined,
      'aria-describedby': describedBy,
      'aria-required': spec.required ? true : undefined,
      placeholder: spec.placeholder,
    };
    // Filled lg wells, as the inspector's fields. `styles.well` dims them when the fieldset is disabled.
    const well = { variant: 'filled', size: 'lg', block: true, className: styles.well } as const;
    let control: ReactNode;
    switch (spec.kind) {
      case 'multiline':
        control = <TextArea {...common} {...well} rows={3} onChange={(e) => change(spec.key, e.target.value)} />;
        break;
      case 'year':
        control = (
          <TextInput {...common} {...well} mono type="text" inputMode="numeric" onChange={(e) => change(spec.key, e.target.value)} />
        );
        break;
      case 'date':
        control = (
          <TextInput
            {...common}
            {...well}
            // A legacy free-text survey date is edited as text, never blanked by a date input.
            type={dateInputFits(base[spec.key]) ? 'date' : 'text'}
            onChange={(e) => change(spec.key, e.target.value)}
          />
        );
        break;
      case 'number':
        control = (
          <TextInput
            {...common}
            {...well}
            mono
            type="number"
            min={0}
            step="any"
            inputMode="decimal"
            // The label already names the unit; the suffix is visual only.
            suffix={spec.unit ? <span aria-hidden="true">{spec.unit}</span> : undefined}
            onChange={(e) => change(spec.key, e.target.value)}
          />
        );
        break;
      default:
        control = <TextInput {...common} {...well} type="text" onChange={(e) => change(spec.key, e.target.value)} />;
    }
    const isHeld = (held as readonly string[]).includes(spec.key);
    return (
      <div className={WIDE.has(spec.key) ? `${styles.fld} ${styles.wide}` : styles.fld}>
        <span className={styles.labelRow}>
          <label className={styles.label} htmlFor={fieldId}>
            {spec.label}
            {spec.unit ? ` (${spec.unit})` : ''}
          </label>
          {isHeld && <span className={styles.tag}>ikke gemt af serveren endnu</span>}
        </span>
        {control}
        {hint && (
          <span id={hintId} className={styles.hint}>
            {hint}
          </span>
        )}
        {error && (
          <span id={errorId} className={styles.error}>
            {error}
          </span>
        )}
      </div>
    );
  }

  return (
    <Portal>
      <FormDialog
        title="Sagsdata"
        subtitle="Grundlag for nedrivningsplanen. Indberettes med rapporten."
        className={styles.dialog}
        noValidate
        onCancel={onClose}
        onSubmit={() => void submit()}
        actions={
          <>
            {canEdit && (
              <p className={status.kind === 'error' || status.kind === 'invalid' ? `${styles.status} ${styles.crit}` : styles.status} role="status">
                {message || (dirty ? 'Ændringer, der ikke er gemt.' : '')}
              </p>
            )}
            {close}
            {canEdit && (
              <>
                <Button size="lg" onClick={undo} disabled={!dirty || saving}>
                  Fortryd
                </Button>
                <Button type="submit" variant="primary" size="lg" className={styles.save} disabled={!dirty || saving}>
                  {saving ? 'Gemmer…' : 'Gem'}
                </Button>
              </>
            )}
          </>
        }
      >
        {!canEdit && <p className={styles.lead}>Du kan se sagsdata, men ikke ændre dem.</p>}
        {held.length > 0 && (
          <p className={styles.stub}>
            Felter mærket »ikke gemt af serveren endnu« findes kun i denne browser. Serveren kan endnu ikke gemme
            BEK 496-felterne; de sendes med, hver gang du gemmer.
          </p>
        )}

        {SAGSDATA_GROUPS.map((group) => (
          <fieldset key={group.id} className={styles.fg} disabled={!canEdit}>
            <legend className={styles.legend}>{group.title}</legend>
            {SAGSDATA_FIELDS.filter((spec) => spec.group === group.id).map((spec) => (
              <Fragment key={spec.key}>
                {renderField(spec)}
                {spec.key === 'demolished_floor_area_m2' && (
                  <div className={`${styles.fld} ${styles.wide}`}>
                    <p className={styles.note} aria-live="polite">
                      {bek ? (
                        <>
                          <b className={bek.required ? styles.warn : undefined}>{bek.lead}</b> · {bek.text}
                        </>
                      ) : (
                        'Angiv det nedrevne etageareal for at se, om der kræves en nedrivningsplan efter BEK 496.'
                      )}
                    </p>
                  </div>
                )}
              </Fragment>
            ))}
          </fieldset>
        ))}
      </FormDialog>
    </Portal>
  );
}
